import { createTrackingConfig, getLabels } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';
import CustomCapaSplitView from './CustomCapaSplitView';

const capaTrackingLabels = getLabels('CAPA');
const capaTrackingBase = createTrackingConfig('CAPA');

export const capaConfig = {
  entityName: 'Capa',
  
  // Entity information for file attachments
  entityTypeId: 3, // CAPA entity type ID (different from Documents which is 1)
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/capa',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      bulkDelete: '/bulk-delete',
      // File attachment endpoints
      fileAttachments: (id) => `/${id}/file-attachments`,
      fileAttachmentCount: (id) => `/${id}/file-attachment-count`,
      withFileAttachments: (id) => `/${id}/with-file-attachments`
    },
    lookupEndpoints: {
      departments: '/department',
      users: '/capa/relations/users',
      capaTypes: '/capa-type',
      capaStatuses: '/capa-status',
      capaSources: '/capa-source',
      priorities: '/priority',
      riskCategories: '/risk-category',
      riskLevels: '/risk-level'
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/capa',
      withFileAttachments: '/capa/with-file-attachments',
      fileAttachments: (entityTypeId) => `/file-attachment/entity/${entityTypeId}/*`
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '30%', // 30% of table width 
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle, // From app config: 'badge'
    tableVariant: 'default',  // 'default' | 'modern' | 'cards' | 'board' | 'timeline' | 'advancedCard'
                            // default: default table with cards
                            // cards: cards table
                            // board: board table
                            // timeline: timeline table
                            // advancedCard: advanced card table
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth, // From app config: '60%'
    
    // Card positioning configuration
    rowPositioning: 'auto', // 'auto', 'fixed', or 'custom'
    customRowOrder: [], // Array of item IDs for custom row ordering
    columnPositioning: 'auto' // 'auto', 'fixed', or 'custom'
  },

  // Custom Split View Panel configuration for CAPA module
  customSplitViewPanel: {
    enabled: true,
    component: CustomCapaSplitView  // Use the custom CAPA split view component
  },

  // Features configuration
  features: {
    fileAttachments: true, // Enable file attachment functionality
    masterEntityTypeId: 3, // CAPA master entity type ID for audit logs
    approvalRequests: true, // Enable approval request functionality
  },

  // Review and Approval configuration
  approval: {
    enabled: true, // Master switch for review and approval functionality
    showInForm: true, // Show review and approval request button in CAPA form
    showInDetails: true, // Show review and approval integration in CAPA details tab
    showInActions: false, // Show review and approval integration in CAPA actions tab
    requireApprovalForClose: false, // Require review and approval before closing CAPA
    allowMultipleApprovals: true, // Allow multiple review and approval requests per CAPA
    defaultApprovalType: 'single', // Default approval type when creating requests
    approvalButtonText: 'Request CAPA Review and Approval', // Text for review and approval request button
    approvalButtonIcon: 'CheckCircleIcon', // Icon component name (from Icons.js) for approval button icon
    showApprovalStatus: true, // Show current review and approval status in CAPA view
    showApprovalActions: true, // Show approval action buttons (approve, reject, etc.)
    showApprovalHistory: true, // Show review and approval history in CAPA timeline
    formMode: 'modal', // Use modal popup for review and approval form
  },
  
  // Custom handlers for CAPA-specific business logic
  customHandlers: {
    onSave: null, // Will be defined in capaPage
    onDelete: null, // Will be defined in capaPage
    onLoad: null, // Backend now provides fileAttachmentCount directly
    onFormOpen: null, // Will be defined in capaPage
    
    // File upload handlers
    onFileUpload: null, // Will handle file upload after CAPA save
    onFileRemove: null, // Will handle file removal
    
    // Board functionality for timeline/board views
    onBoardDrop: async ({ item, fromGroup, toGroup }) => {
      if (toGroup === fromGroup) return;
      await fetch(`/capa/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statusId: Number(toGroup) }),
      });
      // Optionally refresh list afterward
    }
  },
  
  // Service configuration
  service: {
    name: 'capaService',
    methods: {
      getAll: 'getAllCapas',
      getById: 'getCapaById', 
      create: 'createCapa',
      update: 'updateCapa',
      delete: 'deleteCapa',
      validate: 'validateData'
    }
  },
  
  // Display Configuration
  displayName: 'CAPA Management',
  description: 'Corrective and Preventive Action (CAPA) management system for quality improvement',
  icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  
  // Fields to remove from forms (relations, computed, system fields)
  fieldsToRemove: [
    'id', 'department', 'assignedTo', 'owner', 'capaType', 
    'source', 'priority', 'riskCategory', 'riskLevel', 'status',
    'actions', 'attachments', 'timelineEntries', 'approvalSteps', 'createdBy', 
    'updatedBy', 'createdById', 'updatedById', 'createdAt', 'updatedAt', 'closedBy', 'verifiedBy', 
    'linkedEntityType', 'linkedEntityTypeId', 
    'linkedEntityId', 'capaTimelineEntries', 'nonConformityReports', 
    'linkedComplaints', 'linkedDeviations', 'fileAttachments', 'fileAttachmentCount'
  ],

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  fields: [
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'code',
      label: 'CAPA Code',
      type: 'text',
      required: true,
      placeholder: 'Enter CAPA code',
      table: { header: 'Code', width: '120px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 50,
        message: 'CAPA code is required and must not exceed 50 characters'
      }
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter CAPA title',
      table: { header: 'Title', width: '200px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 200,
        message: 'Title is required and must not exceed 200 characters'
      }
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: true,
      placeholder: 'Enter CAPA description',
      rows: 4,
      table: { header: 'Description', width: '300px', sortable: true },
      filterable: false,
      validation: {
        required: true,
        maxLength: 1000,
        message: 'Description is required and must not exceed 1000 characters'
      }
    },
    // File Attachments
    { 
      name: 'fileAttachments', 
      label: 'File(s)', 
      type: 'fileUpload',
      multiple: true,
      accept: '.pdf,.doc,.docx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024, // 50MB
      maxFiles: 10,
      placeholder: 'Drop files here or click to browse',
      table: { 
        header: 'File(s)', 
        width: '50px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount'
      },
      filterable: false,
      validation: {
        required: false
      }
    },
    {
      name: 'department_id',
      label: 'Department',
      type: 'select',
      required: false,
      lookupEndpoint: 'departments',
      options: [],
      table: { 
        header: 'Department', 
        width: '150px', 
        sortable: true,
        displayField: 'department'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid department'
      }
    },
    {
      name: 'assignedToId',
      label: 'Assigned To',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      options: [],
      table: { 
        header: 'Assigned To', 
        width: '150px', 
        sortable: true,
        displayField: 'assignedTo'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid user'
      }
    },
    {
      name: 'ownerId',
      label: 'Owner',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      options: [],
      table: { 
        header: 'Owner', 
        width: '150px', 
        sortable: true,
        displayField: 'owner'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid user'
      }
    },
    {
      name: 'issuedAt',
      label: 'Issued Date',
      type: 'date',
      required: true,
      table: { 
        header: 'Issued', 
        width: tableConfig.dateColumnWidth, // From app config: '120px'
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        required: true,
        message: 'Issued date is required'
      }
    },
    {
      name: 'targetCompletionDate',
      label: 'Target Completion Date',
      type: 'date',
      required: false,
      table: { 
        header: 'Target Completion', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        message: 'Please enter a valid target completion date'
      }
    },
    {
      name: 'actualCompletionDate',
      label: 'Actual Completion Date',
      type: 'date',
      required: false,
      table: { 
        header: 'Actual Completion', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        message: 'Please enter a valid actual completion date'
      }
    },
    {
      name: 'dueDate',
      label: 'Due Date',
      type: 'date',
      required: false,
      table: { 
        header: 'Due Date', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        message: 'Please enter a valid due date'
      }
    },
    {
      name: 'capaTypeId',
      label: 'CAPA Type',
      type: 'select',
      required: false,
      lookupEndpoint: 'capaTypes',
      options: [],
      table: { 
        header: 'Type', 
        width: '130px', 
        sortable: true,
        displayField: 'capaType',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid CAPA type'
      }
    },
    {
      name: 'sourceId',
      label: 'CAPA Source',
      type: 'select',
      required: false,
      lookupEndpoint: 'capaSources',
      options: [],
      table: { 
        header: 'Source', 
        width: '130px', 
        sortable: true,
        displayField: 'source'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid CAPA source'
      }
    },

    {
      name: 'priorityId',
      label: 'Priority',
      type: 'select',
      required: false,
      lookupEndpoint: 'priorities',
      options: [],
      table: { 
        header: 'Priority', 
        width: '100px', 
        sortable: true,
        displayField: 'priority'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid priority'
      }
    },
    {
      name: 'riskLevelId',
      label: 'Risk Level',
      type: 'select',
      required: false,
      lookupEndpoint: 'riskLevels',
      options: [],
      table: { 
        header: 'Risk Level', 
        width: '120px', 
        sortable: true,
        displayField: 'riskLevel'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid risk level'
      }
    },
    {
      name: 'severity',
      label: 'Severity',
      type: 'number',
      required: false,
      min: 1,
      max: 5,
      table: { header: 'Severity', width: '100px', sortable: true },
      filterable: true,
      validation: {
        integer: true,
        min: 1,
        max: 5,
        message: 'Severity must be between 1 and 5'
      }
    },
    {
      name: 'riskCategoryId',
      label: 'Risk Category',
      type: 'select',
      required: false,
      lookupEndpoint: 'riskCategories',
      options: [],
      table: { 
        header: 'Risk Category', 
        width: '130px', 
        sortable: true,
        displayField: 'riskCategory'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid risk category'
      }
    },
    {
      name: 'costEstimate',
      label: 'Cost Estimate',
      type: 'number',
      required: false,
      step: '0.01',
      table: { header: 'Cost Estimate', width: '120px', sortable: true },
      filterable: true,
      validation: {
        min: 0,
        message: 'Cost estimate must be a positive number'
      }
    },
    {
      name: 'actualCost',
      label: 'Actual Cost',
      type: 'number',
      required: false,
      step: '0.01',
      table: { header: 'Actual Cost', width: '120px', sortable: true },
      filterable: true,
      validation: {
        min: 0,
        message: 'Actual cost must be a positive number'
      }
    },
    {
      name: 'recurrence',
      label: 'Recurring Issue',
      type: 'switch',
      required: false,
      defaultValue: false,
      switchLabel: 'Recurring Issue', 
      description: 'Mark if this is a recurring issue', 
      labelPosition: 'left',
      table: { 
        header: 'Recurring', 
        width: '100px',
        sortable: true,
        cellRenderer: 'booleanBadge'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        message: 'Please select recurrence status'
      }
    },
    {
      name: 'containmentRequired',
      label: 'Containment Required',
      type: 'switch',
      required: false,
      defaultValue: false,
      switchLabel: 'Containment Required', 
      description: 'Mark if immediate containment is needed', 
      labelPosition: 'left',
      table: { 
        header: 'Containment', 
        width: '120px',
        sortable: true,
        cellRenderer: 'booleanBadge'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        message: 'Please select containment requirement'
      }
    },
    {
      name: 'rootCause',
      label: 'Root Cause Analysis',
      type: 'textarea',
      required: false,
      placeholder: 'Enter root cause analysis',
      rows: 4,
      table: { header: 'Root Cause', width: '280px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 2000,
        message: 'Root cause analysis must not exceed 2000 characters'
      }
    },
    {
      name: 'containmentActions',
      label: 'Containment Actions',
      type: 'textarea',
      required: false,
      placeholder: 'Enter immediate containment actions taken',
      rows: 3,
      table: { header: 'Containment Actions', width: '280px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Containment actions must not exceed 1000 characters'
      }
    },
    {
      name: 'customerImpact',
      label: 'Customer Impact',
      type: 'switch',
      required: false,
      defaultValue: false,
      switchLabel: 'Customer Impact', 
      description: 'Mark if there is customer impact', 
      labelPosition: 'left',
      table: { 
        header: 'Customer Impact', 
        width: '130px',
        sortable: true,
        cellRenderer: 'booleanBadge'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        message: 'Please select customer impact status'
      }
    },
    {
      name: 'regulatoryImpact',
      label: 'Regulatory Impact',
      type: 'switch',
      required: false,
      defaultValue: false,
      switchLabel: 'Regulatory Impact', 
      description: 'Mark if there is regulatory impact', 
      labelPosition: 'left',
      table: { 
        header: 'Regulatory Impact', 
        width: '140px',
        sortable: true,
        cellRenderer: 'booleanBadge'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        message: 'Please select regulatory impact status'
      }
    },
    {
      name: 'businessImpact',
      label: 'Business Impact Description',
      type: 'textarea',
      required: false,
      placeholder: 'Describe the business impact',
      rows: 3,
      table: { header: 'Business Impact', width: '280px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Business impact description must not exceed 1000 characters'
      }
    },
    {
      name: 'financialImpact',
      label: 'Financial Impact',
      type: 'number',
      required: false,
      step: '0.01',
      table: { header: 'Financial Impact', width: '140px', sortable: true },
      filterable: true,
      validation: {
        min: 0,
        message: 'Financial impact must be a positive number'
      }
    },
    {
      name: 'statusId',
      label: 'Status',
      type: 'select',
      required: true,
      lookupEndpoint: 'capaStatuses',
      options: [],
      table: { 
        header: 'Status', 
        width: '120px', 
        sortable: true,
        displayField: 'status',
        cellRenderer: 'statusBadge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        integer: true,
        message: 'Please select a valid status'
      }
    },
    {
      name: 'closedAt',
      label: 'Closed Date',
      type: 'date',
      required: false,
      readonly: true,
      table: { 
        header: 'Closed', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    {
      name: 'closureComments',
      label: 'Closure Comments',
      type: 'textarea',
      required: false,
      placeholder: 'Enter closure comments',
      rows: 3,
      table: { header: 'Closure Comments', width: '300px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Closure comments must not exceed 1000 characters'
      }
    },
    {
      name: 'effectivenessVerified',
      label: 'Effectiveness Verified',
      type: 'switch',
      required: false,
      defaultValue: false,
      switchLabel: 'Effectiveness Verified', 
      description: 'Mark if effectiveness has been verified', 
      labelPosition: 'left',
      table: { 
        header: 'Verified', 
        width: '100px',
        sortable: true,
        cellRenderer: 'booleanBadge'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        message: 'Please select verification status'
      }
    },
    {
      name: 'verifiedAt',
      label: 'Verified Date',
      type: 'date',
      required: false,
      readonly: true,
      table: { 
        header: 'Verified Date', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    {
      name: 'verificationComments',
      label: 'Verification Comments',
      type: 'textarea',
      required: false,
      placeholder: 'Enter verification comments',
      rows: 3,
      table: { header: 'Verification Comments', width: '300px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Verification comments must not exceed 1000 characters'
      }
    },
    {
      name: 'verificationMethod',
      label: 'Verification Method',
      type: 'text',
      required: false,
      placeholder: 'How was effectiveness verified?',
      table: { header: 'Verification Method', width: '220px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 255,
        message: 'Verification method must not exceed 255 characters'
      }
    },
    {
      name: 'nextReviewDate',
      label: 'Next Review Date',
      type: 'date',
      required: false,
      table: { 
        header: 'Next Review', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
  ],

  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,  // Use displayField as key if available
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name  // Keep reference to original field name
      }));
    
    return columns;
  },

  exportFileName: 'qms_capas',

  // Backend field mappings
  fieldMappings: {
    // Map frontend display fields to backend field paths
    department: 'department.name',
    assignedTo: 'assignedTo.userDisplayName',
    owner: 'owner.userDisplayName',
    capaType: 'capaType.name',
    source: 'source.name',
    priority: 'priority.name',
    riskCategory: 'riskCategory.name',
    riskLevel: 'riskLevel.name',
    status: 'status.name',
    closedBy: 'closedBy.userDisplayName',
    verifiedBy: 'verifiedBy.userDisplayName',
    createdBy: 'createdBy.userDisplayName',
    updatedBy: 'updatedBy.userDisplayName'
  },
  
  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'code', 'title', 'statusId', 'capaTypeId', 'priorityId', 
        'assignedToId', 'ownerId', 'department_id'
      ]
    },
    {
      name: 'dates',
      label: 'Dates & Timeline',
      icon: '📅',
      fields: [
        'issuedAt', 'targetCompletionDate', 'actualCompletionDate', 
        'dueDate', 'closedAt', 'verifiedAt', 'nextReviewDate'
      ]
    },
    {
      name: 'classification',
      label: 'Classification & Risk',
      icon: '🏷️',
      fields: [
        'sourceId', 'riskLevelId', 'riskCategoryId', 'severity',
        'recurrence', 'containmentRequired'
      ]
    },
    {
      name: 'impact',
      label: 'Impact & Financial',
      icon: '⚠️',
      fields: [
        'customerImpact', 'regulatoryImpact', 'financialImpact',
        'costEstimate', 'actualCost', 'effectivenessVerified'
      ]
    }
  ],

  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Basic Information',
      icon: '📄',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['code', 'title', 'description'],
      fieldSpans: {
        'code': 'md:col-span-4',
        'title': 'md:col-span-8', 
        'description': 'md:col-span-12'
      }
    },
    {
      title: 'Assignment & Ownership',
      icon: '👥',
      layout: 'grid',
      gridCols: 'md:grid-cols-3',
      fields: ['department_id', 'assignedToId', 'ownerId']
    },
    {
      title: 'Dates & Timeline',
      icon: '📅',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['issuedAt', 'targetCompletionDate', 'actualCompletionDate', 'dueDate', 'nextReviewDate']
    },
    {
      title: 'Classification & Risk Assessment',
      icon: '🏷️',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['capaTypeId', 'sourceId', 'priorityId', 'riskLevelId', 'riskCategoryId', 'severity'],
      fieldSpans: {
        'capaTypeId': 'md:col-span-4',
        'sourceId': 'md:col-span-4',
        'priorityId': 'md:col-span-4',
        'riskLevelId': 'md:col-span-4',
        'riskCategoryId': 'md:col-span-4',
        'severity': 'md:col-span-4'
      }
    },
    {
      title: 'Analysis',
      icon: '🔍',
      layout: 'grid',
      gridCols: 'md:grid-cols-1',
      fields: ['rootCause', 'containmentActions']
    },
    {
      title: 'Impact Assessment',
      icon: '⚠️',
      layout: 'grid',
      gridCols: 'md:grid-cols-3',
      fields: ['customerImpact', 'regulatoryImpact', 'financialImpact', 'businessImpact'],
      fieldSpans: {
        'customerImpact': 'md:col-span-1',
        'regulatoryImpact': 'md:col-span-1',
        'financialImpact': 'md:col-span-1',
        'businessImpact': 'md:col-span-3',        
      }

    },
    {
      title: 'Financial Information',
      icon: '💰',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['costEstimate', 'actualCost']
    },
    {
      title: 'Status',
      icon: '⚡',
      layout: 'grid',
      gridCols: 'md:grid-cols-3',
      fields: ['statusId', 'recurrence', 'containmentRequired'],
      fieldSpans: {
        'statusId': 'md:col-span-1',
        'recurrence': 'md:col-span-1',
        'containmentRequired': 'md:col-span-1',
      }
    },
    {
      title: 'Closure',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['closedAt', 'closedById', 'closureComments'],
      fieldSpans: {
        'closedAt': 'md:col-span-4',
        'closedById': 'md:col-span-4',
        'closureComments': 'md:col-span-12',
      }
    },
    {
      title: 'Verification',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['effectivenessVerified', 'verifiedAt', 'verifiedById', 'nextReviewDate', 'verificationComments', 'verificationMethod'],
      fieldSpans: {
        'effectivenessVerified': 'md:col-span-4',
        'verifiedAt': 'md:col-span-4',
        'verifiedById': 'md:col-span-4',
        'nextReviewDate': 'md:col-span-4',
        'verificationComments': 'md:col-span-12',
        'verificationMethod': 'md:col-span-12'
      }
    },
    {
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments']
    }
    
  ],

  // Tracking Section — show audit users by display name (user objects from API / selected row)
  trackingSection: {
    ...capaTrackingBase,
    fields: capaTrackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: capaTrackingLabels.createdDateLabel,
            userEmptyText: capaTrackingLabels.notSpecifiedText,
            dateEmptyText: capaTrackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: capaTrackingLabels.updatedDateLabel,
              userEmptyText: capaTrackingLabels.notModifiedText,
              dateEmptyText: capaTrackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  // Labels and Text Configuration (uses predefined CAPA labels + custom overrides)
  labels: {
    // Using predefined CAPA labels from centralized labels file
    // You can override any label by uncommenting and modifying:
    singular: 'CAPA',
    plural: 'CAPAs',
    pageTitle: 'CAPA Management',
    pageSubtitle: 'Corrective and Preventive Action management for quality improvement',
    searchPlaceholder: 'Search CAPAs by code or title...',
    viewTitle: 'View CAPA',
    noSelectionText: 'Select a CAPA to see details'
  },

  // Custom handlers for CAPA-specific business logic
  // (moved earlier to merge with onBoardDrop)

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };

    // Remove fileAttachments from the main CAPA data (handled separately)
    delete transformed.fileAttachments;
    
    // Convert foreign key fields to integers or remove if empty
    const foreignKeyFields = [
      'department_id', 'assignedToId', 'ownerId', 'capaTypeId', 
      'sourceId', 'priorityId', 'riskLevelId', 'riskCategoryId', 'statusId'
    ];
    
    foreignKeyFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert integer fields
    const integerFields = ['severity'];
    integerFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert decimal fields
    const decimalFields = ['costEstimate', 'actualCost', 'financialImpact'];
    decimalFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseFloat(transformed[field]);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert date fields to proper Date objects
    const dateFields = [
      'issuedAt', 'targetCompletionDate', 'actualCompletionDate', 
      'dueDate', 'closedAt', 'verifiedAt', 'nextReviewDate'
    ];
    
    dateFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          // Convert date string to Date object
          const dateValue = new Date(transformed[field]);
          if (!isNaN(dateValue.getTime())) {
            transformed[field] = dateValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });
    
    return transformed;
  },

  // Notification messages
  messages: {
    loading: 'Loading CAPAs...',
    noData: 'No CAPAs found. Create your first CAPA to get started.',
    createSuccess: 'CAPA created successfully!',
    updateSuccess: 'CAPA updated successfully!',
    deleteSuccess: 'CAPA deleted successfully!',
    createError: 'Failed to create CAPA',
    updateError: 'Failed to update CAPA', 
    deleteError: 'Failed to delete CAPA',
    loadError: 'Failed to load CAPAs',
    validationError: 'Validation failed'
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null, // Will be set after validation module loads

  // Help text for field descriptions
  helpText: {
    code: { short: 'Unique CAPA identifier used for tracking and reference.' },
    title: { short: 'Brief, descriptive title of the CAPA issue or action.' },
    description: { short: 'Detailed description of the problem, issue, or improvement opportunity.' },
    fileAttachments: { short: 'Supporting documents, evidence, or related files for this CAPA.' },
    department_id: { short: 'Department responsible for managing this CAPA.' },
    assignedToId: { short: 'Person assigned to work on this CAPA.' },
    ownerId: { short: 'Person ultimately responsible for this CAPA completion.' },
    issuedAt: { short: 'Date when the CAPA was formally issued or initiated.' },
    targetCompletionDate: { short: 'Planned completion date for this CAPA.' },
    actualCompletionDate: { short: 'Actual date when the CAPA was completed.' },
    dueDate: { short: 'Final deadline for CAPA completion.' },
    capaTypeId: { short: 'Type of CAPA: Corrective Action (fix existing problem) or Preventive Action (prevent future problem).' },
    sourceId: { short: 'Origin or trigger that led to this CAPA (audit, complaint, non-conformity, etc.).' },
    priorityId: { short: 'Priority level based on risk and urgency.' },
    riskLevelId: { short: 'Risk level assessment for this CAPA.' },
    severity: { short: 'Severity rating from 1 (low) to 5 (critical).' },
    riskCategoryId: { short: 'Category of risk associated with this CAPA.' },
    costEstimate: { short: 'Estimated cost to implement this CAPA.' },
    actualCost: { short: 'Actual cost incurred in implementing this CAPA.' },
    recurrence: { short: 'Indicates if this is a recurring issue that has happened before.' },
    containmentRequired: { short: 'Whether immediate containment actions are needed to prevent further issues.' },
    rootCause: { short: 'Analysis of the underlying cause that led to this issue.' },
    containmentActions: { short: 'Immediate actions taken to contain or stop the problem.' },
    customerImpact: { short: 'Whether this issue affects customers or end users.' },
    regulatoryImpact: { short: 'Whether this issue has regulatory compliance implications.' },
    businessImpact: { short: 'Description of how this issue affects business operations.' },
    financialImpact: { short: 'Financial cost or impact of this issue.' },
    statusId: { short: 'Current status of the CAPA in the workflow.' },
    closedAt: { short: 'Date when the CAPA was officially closed.' },
    closureComments: { short: 'Comments explaining why and how the CAPA was closed.' },
    effectivenessVerified: { short: 'Whether the effectiveness of the CAPA has been verified.' },
    verifiedAt: { short: 'Date when effectiveness verification was completed.' },
    verificationComments: { short: 'Comments about the verification process and results.' },
    verificationMethod: { short: 'Method used to verify the effectiveness of the CAPA.' },
    nextReviewDate: { short: 'Date for the next scheduled review of this CAPA.' }
  },

  // Helper methods for approval functionality
  isApprovalEnabled: function() {
    return this.features.approvalRequests && this.approval.enabled;
  },

  shouldShowApprovalInForm: function() {
    return this.isApprovalEnabled() && this.approval.showInForm;
  },

  shouldShowApprovalInDetails: function() {
    return this.isApprovalEnabled() && this.approval.showInDetails;
  },

  shouldShowApprovalInActions: function() {
    return this.isApprovalEnabled() && this.approval.showInActions;
  },

  requiresApprovalForClose: function() {
    return this.isApprovalEnabled() && this.approval.requireApprovalForClose;
  }
};

export default capaConfig;
